import moment from 'moment';
/**
 * HybridTimeService is a service class that manages hybrid time-related functionalities.
 * It interacts with various services and utilities to determine time-based conditions and states.
 */
class HybridTimeService {
    /**
     * Constructor for HybridTimeService.
     * @param {Object} $bridges - The bridges service.
     * @param {Object} TaskInfoService - The task info service.
     * @param {Object} $location - The location service.
     */
    constructor($bridges, TaskInfoService, $location) {
        this.$bridges = $bridges;
        this.TaskInfoService = TaskInfoService;
        this.$location = $location;

        this._unixTimeStampUTC = undefined;
        this._taskInfo = TaskInfoService.getTaskInfo();
        console.log('HybridTimeService constructor', this._taskInfo);
        this._hybridStarted = false;
        this._secondaryAccessCode = false;
        this._logIdentifier = 'HybridTimeService';
    }

    /**
     * Sets the Unix timestamp in UTC.
     * @param {number|string} value - The Unix timestamp value to set.
     */
    setUnixTimeStampUTC(value) {
        this._hybridStarted = true;
        value = parseInt(value);
        this._unixTimeStampUTC = isNaN(value) || value === 0 ? undefined : value;
    }

    /**
     * Checks if the Unix timestamp is set.
     * @returns {boolean} - True if the Unix timestamp is set, otherwise false.
     */
    existsUnixTimeStamp() {
        return this._unixTimeStampUTC !== undefined;
    }

    /**
     * Checks if the hybrid time has begun.
     * @returns {boolean} - True if the hybrid time has begun, otherwise false.
     */
    isBegun() {
        if (!this._taskInfo) return true;
        const bd = this.safeDate(this._taskInfo.getBegin());
        if (bd == null) return true;
        if (!this.existsUnixTimeStamp()) return true;
        return new Date(this._unixTimeStampUTC * 1000).getTime() >= bd.getTime();
    }

    /**
     * Checks if the hybrid time has expired.
     * @returns {boolean} - True if the hybrid time has expired, otherwise false.
     */
    isExpired() {
        if (!this._taskInfo) return false;
        const ed = this.safeDate(this._taskInfo.getExpire());
        if (ed == null) return false;
        if (!this.existsUnixTimeStamp()) return false;
        console.log('HybridTimeService isExpired', ed.getTime(), new Date(this._unixTimeStampUTC * 1000).getTime());
        return ed.getTime() <= new Date(this._unixTimeStampUTC * 1000).getTime();
    }

    /**
     * Checks if the current time is within the hybrid timeframe.
     * @returns {boolean} - True if the current time is within the timeframe, otherwise false.
     */
    isInTimeframe() {
        console.log('HybridTimeService isInTimeframe', this.existsUnixTimeStamp(), this.isBegun(), this.isExpired());
        if (!this.existsUnixTimeStamp()) return true;
        return this.isBegun() && !this.isExpired();
    }

    /**
     * Checks if the hybrid time has started.
     * @returns {boolean} - True if the hybrid time has started, otherwise false.
     */
    isHybridStarted() {
        return this._hybridStarted;
    }

    /**
     * Checks if the current time is within the hybrid timeframe and redirects if necessary.
     */
    isInTimeframeWithRedirect() {
        if (this._secondaryAccessCode) {
            if (this.existsUnixTimeStamp() && this.isHybridStarted() && this.isSecondaryAccessExpired()) {
                this.$location.path("/hybrid-time");
            }
            return;
        }
        if (this.existsUnixTimeStamp() && this.isHybridStarted() && !this.isInTimeframe()) {
            console.log('HybridTimeService isInTimeframeWithRedirect', this.isInTimeframe(), this.isHybridStarted());
            this.$location.path("/hybrid-time");
        }
    }

    /**
     * Checks if the secondary access has expired.
     * @returns {boolean} - True if the secondary access has expired, otherwise false.
     */
    isSecondaryAccessExpired() {
        if (!this._taskInfo) return false;
        const ed = this.safeDate(this._taskInfo.getExpire());
        if (ed == null) return false;
        return this.isExpired() && !moment(ed).add(this._taskInfo.getAfterAssessmentStart(), 'hours').isAfter(moment(new Date(this._unixTimeStampUTC * 1000)));
    }

    /**
     * Calculates the difference in hours between the current time and the Unix timestamp.
     * @returns {number} - The difference in hours.
     */
    differenceHour() {
        return Math.floor((moment(new Date(this._unixTimeStampUTC * 1000)).unix() - moment().unix()) / 3600);
    }

    /**
     * Activates the secondary access code.
     */
    activateSecondaryAccessCode() {
        this._secondaryAccessCode = true;
    }

    /**
     * Creates the current timezone offset.
     * @returns {string} - The current timezone offset.
     */
    createCurrentTimeZoneOffset() {
        return moment().parseZone(new Date().toString()).format('Z');
    }

    /**
     * Safely converts a date string to a Date object, considering the current timezone offset.
     * @param {string} dateStr - The date string to convert.
     * @returns {Date|null} - The Date object or null if the date string is invalid.
     */
    safeDate(dateStr) {
        if (dateStr == null || dateStr === '') return null;
        return new Date(dateStr + this.createCurrentTimeZoneOffset());
    }
}

export default HybridTimeService;